import { type ReactNode } from 'react';

import {
  AccreditationMarquee,
  CinematicHero,
  ConstellationHero,
  ExperienceHorizontal,
  FeatureSticky,
  HeroGallery,
  MediaMosaic,
  ProgrammeShowcase,
  ProofStats,
  SerenityIntro,
  WordHero,
} from '@/components/sections';
import { IBASTestimonials, JourneyRoadmap, MegaNav, WaveDivider } from '@/components/ui';

/** A titled band wrapping one showcased component. */
function Exhibit({ name, note, children }: { name: string; note: string; children: ReactNode }) {
  return (
    <section className="border-b border-border">
      <div className="mx-auto max-w-6xl px-6 py-6">
        <p className="font-label text-xs tracking-[0.2em] text-brand-gold uppercase">{name}</p>
        <p className="mt-1 text-sm text-muted-foreground">{note}</p>
      </div>
      <div>{children}</div>
    </section>
  );
}

/**
 * Component showcase — every premium section/UI block in isolation, as a living
 * catalogue of directions to build more from. Not linked in primary nav.
 */
export function ShowcasePage() {
  return (
    <div className="min-h-dvh bg-background">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <p className="font-label text-xs tracking-[0.2em] text-brand-gold uppercase">
          IBAS · Component library
        </p>
        <h1 className="mt-2 font-display text-4xl font-semibold sm:text-5xl">
          Premium motion components
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Each block below is custom-built with GSAP and anime.js, branded to IBAS, and degrades
          gracefully under reduced motion. Use them as the starting palette for new pages.
        </p>
      </div>

      <Exhibit name="MegaNav" note="Floating-pill navigation with sliding programme panels.">
        <div className="py-6">
          <MegaNav />
        </div>
      </Exhibit>
      <Exhibit name="CinematicHero" note="Video hero · pointer parallax · scroll fade-out.">
        <CinematicHero />
      </Exhibit>
      <Exhibit name="MediaMosaic" note="Interactive campus gallery · cursor tilt + scroll reveal.">
        <MediaMosaic />
      </Exhibit>
      <Exhibit name="FeatureSticky" note="Scroll sticky-media feature with stepping content.">
        <FeatureSticky />
      </Exhibit>
      <Exhibit name="ExperienceHorizontal" note="Pinned horizontal-scroll experience panels.">
        <ExperienceHorizontal />
      </Exhibit>
      <Exhibit
        name="ConstellationHero"
        note="Interactive canvas particle field · pointer repulsion."
      >
        <ConstellationHero />
      </Exhibit>
      <Exhibit name="WordHero" note="GSAP scroll-pinned word-cycling hero.">
        <WordHero />
      </Exhibit>
      <Exhibit name="HeroGallery" note="Scroll-scrubbed bento gallery hero.">
        <HeroGallery />
      </Exhibit>
      <Exhibit name="ProofStats" note="Count-up proof figures with year/scope captions.">
        <ProofStats />
      </Exhibit>
      <Exhibit name="AccreditationMarquee" note="Seamless accreditation ticker (EduQua text-only).">
        <AccreditationMarquee />
      </Exhibit>
      <Exhibit name="ProgrammeShowcase" note="Programme cards with pointer 3D-tilt + reveal.">
        <ProgrammeShowcase />
      </Exhibit>
      <Exhibit name="WaveDivider" note="Pointer-reactive SVG wave section divider.">
        <WaveDivider />
      </Exhibit>
      <Exhibit name="IBASTestimonials" note="Counter-rotating reel + per-character text rise.">
        <IBASTestimonials />
      </Exhibit>
      <Exhibit name="JourneyRoadmap" note="Admissions journey timeline.">
        <JourneyRoadmap />
      </Exhibit>
      <Exhibit name="SerenityIntro" note="Vision statement · word-appear + pointer glow.">
        <SerenityIntro />
      </Exhibit>
    </div>
  );
}
